import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Pencil, Users, Clock, GraduationCap, FileDown, FileText, Smartphone, QrCode, Sparkles, AlertTriangle, Loader2, Eye, EyeOff, Mail, FileSignature, Download, CheckCircle2, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { Header, Field, Empty } from "@/pages/Vehicles";
import { FileUpload, AttachmentThumbs } from "@/components/FileUpload";
import { DatePicker } from "@/components/DatePicker";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { downloadPdf } from "@/lib/download";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const empty = { name: "", licence_number: "", licence_expiry: "", cpc_expiry: "", tacho_card_expiry: "", licence_check_date: "", licence_check_code: "", penalty_points: 0, licence_check_due: "", weekly_hours: 0, max_weekly_hours: 56, opt_out_signed: false, opt_out_date: "", opt_out_withdrawn_date: "", opt_out_attachments: [], assigned_vehicle_reg: "", vehicle_locked: false, notes: "", start_date: "", leave_date: "", nationality: "", rtw_status: "", rtw_document_type: "", rtw_expiry: "", rtw_verified_date: "", rtw_verified_by: "", rtw_attachments: [], date_of_birth: "", ni_ppsn: "", personal_phone: "", personal_email: "", home_address: "", emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_phone: "", p45_provided: false, tax_code: "", starter_checklist_statement: "", taxable_benefits: "", student_loan_plan: "", postgraduate_loan: false, bank_account_holder: "", bank_name: "", bank_sort_code: "", bank_account_number: "", starter_attachments: [], engagement_type: "Employee", company_name: "", company_reg_number: "", company_utr: "", company_vat_number: "", company_address: "", ir35_status: "", licence_issue_number: "", licence_categories: "", licence_categories_provisional: "", endorsement_history: "", tacho_card_number: "", vat_registered: false, vat_number: "" };
const todayISO = () => new Date().toISOString().slice(0, 10);
const isLeftDriver = (d) => !!d.leave_date && String(d.leave_date).slice(0, 10) < todayISO();
const DRIVER_DOC_TYPES = ["Driving Licence", "Driver CPC", "Tacho Card", "Driver Infringement", "Infringement Report", "Warning Letter", "Attestation Record", "Indoctrination Document", "Adhoc Note", "Other"];
const emptyDoc = { title: "", doc_type: "Driver Infringement", reference: "", expiry_date: "", notes: "", attachments: [] };
const lcEmpty = () => ({ check_date: new Date().toISOString().slice(0, 10), check_code: "", points: 0, result: "clean", next_check_due: "", notes: "" });
const LC_RESULTS = [["clean", "Clean licence"], ["points", "Points / endorsements"], ["disqualified", "Disqualified"], ["other", "Other"]];
const RTW_STATUSES = ["British/Irish citizen", "EU/EEA citizen", "Visa", "Work permit", "Share code"];
const RTW_DOC_TYPES = ["Passport", "Share code", "Visa", "Work permit", "Other"];
const RTW_PERMANENT = ["British/Irish citizen", "EU/EEA citizen"];
const STARTER_STATEMENTS = [["A", "A — First job since 6 April, no other taxable income"], ["B", "B — Only job now, but had another this tax year"], ["C", "C — Has another job or pension"]];
const STUDENT_LOAN_PLANS = ["Plan 1", "Plan 2", "Plan 4", "Plan 5"];
const ENGAGEMENT_TYPES = ["Employee", "Subcontractor (Ltd co.)"];

export default function Drivers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isIE = user?.region === "IE";
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [training, setTraining] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [docFor, setDocFor] = useState(null);
  const [docForm, setDocForm] = useState(emptyDoc);
  const [cpcFor, setCpcFor] = useState(null);
  const [cpcForm, setCpcForm] = useState({ course_name: "", hours: "", completed_date: new Date().toISOString().slice(0, 10), provider: "" });
  const [qrFor, setQrFor] = useState(null);
  const [lcFor, setLcFor] = useState(null);
  const [lcForm, setLcForm] = useState(lcEmpty());
  const [lcHistory, setLcHistory] = useState([]);
  const [tab, setTab] = useState("active");
  const [scanOpen, setScanOpen] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [showAcct, setShowAcct] = useState(false);
  const [sbi, setSbi] = useState({ open: false, driver: null, vat: false, cis: false, cis_rate: 20, haulage: false });
  const openSbi = (d, haulage = false) => setSbi({ open: true, driver: d, vat: !!d.company_vat_number, cis: false, cis_rate: 20, haulage });
  const genSbi = () => {
    const q = new URLSearchParams({ driver_id: sbi.driver.id, vat: String(sbi.vat), cis: String(sbi.cis), cis_rate: String(sbi.cis_rate), haulage: String(sbi.haulage) });
    downloadPdf(`/self-billing-invoice?${q.toString()}`, `self-billing-${(sbi.driver.name || "invoice").replace(/ /g, "_")}.pdf`);
    setSbi((s) => ({ ...s, open: false }));
  };
  const [hist, setHist] = useState({ open: false, driver: null, items: [], loading: false });
  const openHist = async (d) => {
    setHist({ open: true, driver: d, items: [], loading: true });
    const { data } = await api.get(`/self-billing-invoices?driver_id=${d.id}`);
    setHist((h) => ({ ...h, items: data, loading: false }));
  };
  const refreshHist = async () => {
    const { data } = await api.get(`/self-billing-invoices?driver_id=${hist.driver.id}`);
    setHist((h) => ({ ...h, items: data }));
  };
  const togglePaid = async (id) => { await api.post(`/self-billing-invoices/${id}/toggle-paid`); refreshHist(); };
  const delInvoice = async (id) => { await api.delete(`/self-billing-invoices/${id}`); refreshHist(); };
  const emailInvoice = async (rec) => {
    const to = window.prompt("Send invoice to email:", rec.haulier_email || "");
    if (!to) return;
    try { await api.post(`/self-billing-invoices/${rec.id}/email`, { to }); toast.success(`Invoice emailed to ${to}`); refreshHist(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
  };

  const [agr, setAgr] = useState({ open: false, driver: null, currency: "GBP", sending: false });
  const openAgr = (d) => setAgr({ open: true, driver: d, currency: d.default_currency || (isIE ? "EUR" : "GBP"), sending: false });
  const genAgr = () => {
    const q = new URLSearchParams({ driver_id: agr.driver.id, currency: agr.currency });
    downloadPdf(`/self-billing-agreement?${q.toString()}`, `self-billing-agreement-${(agr.driver.name || "driver").replace(/ /g, "_")}.pdf`);
    setAgr((s) => ({ ...s, open: false }));
  };
  const sendAgrToSign = async () => {
    setAgr((s) => ({ ...s, sending: true }));
    try {
      await api.post("/form-assignments", {
        driver_id: agr.driver.id, source_type: "self_billing_agreement", source_key: "self_billing_agreement",
        currency: agr.currency, title: "Self-Billing Services Agreement",
      });
      toast.success(`Sent to ${agr.driver.name} to sign in the driver app`);
      setAgr((s) => ({ ...s, open: false, sending: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send for signing");
      setAgr((s) => ({ ...s, sending: false }));
    }
  };

  const [ts, setTs] = useState({ open: false, driver: null, currency: "GBP", week_ending: "", rate: "", rate_basis: "hour", qty: "", sending: false });
  const openTs = (d) => setTs({ open: true, driver: d, currency: d.default_currency || (isIE ? "EUR" : "GBP"), week_ending: "", rate: "", rate_basis: "hour", qty: "", sending: false });
  const tsParams = () => {
    const p = { driver_id: ts.driver.id, currency: ts.currency, rate_basis: ts.rate_basis };
    if (ts.week_ending) p.week_ending = ts.week_ending;
    if (ts.rate !== "") p.rate = ts.rate;
    if (ts.qty !== "") p.qty = ts.qty;
    return new URLSearchParams(p);
  };
  const genTs = () => {
    downloadPdf(`/weekly-timesheet?${tsParams().toString()}`, `weekly-timesheet-${(ts.driver.name || "driver").replace(/ /g, "_")}.pdf`);
    setTs((s) => ({ ...s, open: false }));
  };
  const sendTsToSign = async () => {
    setTs((s) => ({ ...s, sending: true }));
    try {
      await api.post("/form-assignments", {
        driver_id: ts.driver.id, source_type: "weekly_timesheet", source_key: "weekly_timesheet",
        currency: ts.currency, week_ending: ts.week_ending, rate: String(ts.rate), rate_basis: ts.rate_basis,
        qty: String(ts.qty), title: "Weekly Driver Time Sheet",
      });
      toast.success(`Sent to ${ts.driver.name} to sign in the driver app`);
      setTs((s) => ({ ...s, open: false, sending: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send for signing");
      setTs((s) => ({ ...s, sending: false }));
    }
  };

  const [docs, setDocs] = useState({ open: false, driver: null, list: null });
  const openDocs = async (d) => {
    setDocs({ open: true, driver: d, list: null });
    try {
      const { data } = await api.get(`/form-assignments?driver_id=${d.id}`);
      setDocs((s) => ({ ...s, list: data }));
    } catch { setDocs((s) => ({ ...s, list: [] })); toast.error("Could not load documents"); }
  };
  const invoiceFromTimesheet = (a, driver) => {
    const fmtUK = (iso) => { try { const [y, m, dd] = String(iso).slice(0, 10).split("-"); return `${dd}/${m}/${y}`; } catch { return iso; } };
    let from = "";
    if (a.week_ending) { const dt = new Date(a.week_ending); dt.setDate(dt.getDate() - 6); from = dt.toISOString().slice(0, 10); }
    const basisDay = a.rate_basis === "day";
    const label = a.week_ending ? `Driving services — week ending ${fmtUK(a.week_ending)}` : "Driving services";
    navigate("/invoices", { state: { prefill: {
      driver_id: driver.id, source_assignment_id: a.id,
      period_from: from, period_to: (a.week_ending || "").slice(0, 10),
      line_items: [{ description: basisDay ? `${label} (${a.qty || ""} days)`.trim() : label, qty: a.qty || "", rate: a.rate || "", amount: "" }],
    } } });
  };

  const load = async () => {
    const [d, t, docs, v] = await Promise.all([api.get("/drivers"), api.get("/training"), api.get("/documents"), api.get("/vehicles")]);
    setItems(d.data); setTraining(t.data); setDocuments(docs.data.filter((x) => x.driver_id)); setVehicles(v.data.map((x) => x.registration));
  };
  const driverTraining = (d) => training.filter((t) => (t.driver_id && t.driver_id === d.id) || t.driver_name === d.name);
  const driverDocs = (d) => documents.filter((x) => x.driver_id === d.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const activeItems = items.filter((d) => !isLeftDriver(d));
  const archivedItems = items.filter((d) => isLeftDriver(d));
  const shown = tab === "archived" ? archivedItems : activeItems;

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (d) => {
    setForm({ ...empty, ...d, licence_expiry: d.licence_expiry || "", cpc_expiry: d.cpc_expiry || "", tacho_card_expiry: d.tacho_card_expiry || "", licence_check_date: d.licence_check_date || "", licence_check_due: d.licence_check_due || "", start_date: d.start_date || "", leave_date: d.leave_date || "", rtw_expiry: d.rtw_expiry || "", rtw_verified_date: d.rtw_verified_date || "", rtw_attachments: d.rtw_attachments || [], opt_out_signed: !!d.opt_out_signed, opt_out_date: d.opt_out_date || "", opt_out_withdrawn_date: d.opt_out_withdrawn_date || "", opt_out_attachments: d.opt_out_attachments || [], date_of_birth: d.date_of_birth || "" });
    setEditId(d.id); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, weekly_hours: Number(form.weekly_hours), max_weekly_hours: Number(form.max_weekly_hours), penalty_points: Number(form.penalty_points) };
    ["licence_expiry", "cpc_expiry", "tacho_card_expiry", "licence_check_date", "licence_check_due", "start_date", "leave_date", "rtw_expiry", "rtw_verified_date", "opt_out_date", "opt_out_withdrawn_date", "date_of_birth"].forEach((k) => { if (!payload[k]) payload[k] = null; });
    try {
      if (editId) await api.put(`/drivers/${editId}`, payload);
      else await api.post("/drivers", payload);
      toast.success(editId ? "Driver updated" : "Driver added");
      setOpen(false); load();
    } catch { toast.error("Could not save driver"); }
  };
  const remove = async (id) => { await api.delete(`/drivers/${id}`); toast.success("Driver removed"); load(); };

  const createDriverDraft = async (fields, uncertain = []) => {
    try {
      await api.post("/drivers", { ...empty, ...fields, ai_extracted: true, needs_review: true, review_fields: uncertain });
      toast.success("Draft driver created — open it to confirm the details");
      setOpen(false); load();
    } catch { toast.error("Could not create the draft driver"); }
  };

  const bulkScan = async (files) => {
    if (!files?.length) return;
    setScanBusy(true); setScanResults(null);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const res = await api.post("/drivers/ai-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanResults(res.data);
      toast.success(`${res.data.count} driver${res.data.count === 1 ? "" : "s"} created from licences`);
      load();
    } catch { toast.error("Bulk licence scan failed"); }
    finally { setScanBusy(false); }
  };

  const issueCode = async (d) => {
    try {
      const { data } = await api.post(`/drivers/${d.id}/access-code`);
      toast.success(`Access code: ${data.access_code}`);
      load();
    } catch { toast.error("Could not generate code"); }
  };

  const openDoc = (d) => { setDocFor(d); setDocForm(emptyDoc); };
  const saveDoc = async () => {
    try {
      await api.post("/documents", { ...docForm, driver_id: docFor.id, driver_name: docFor.name, expiry_date: docForm.expiry_date || null });
      toast.success("Document added"); setDocFor(null); load();
    } catch { toast.error("Could not save document"); }
  };
  const removeDoc = async (id) => { await api.delete(`/documents/${id}`); toast.success("Document removed"); load(); };

  const openCpc = (d) => { setCpcFor(d); setCpcForm({ course_name: "", hours: "", completed_date: new Date().toISOString().slice(0, 10), provider: "" }); };
  const saveCpc = async () => {
    if (!cpcForm.course_name || !cpcForm.hours) { toast.error("Enter a course and hours"); return; }
    if (!cpcForm.completed_date) { toast.error("Enter the completed date"); return; }
    try {
      await api.post("/training", {
        driver_id: cpcFor.id, driver_name: cpcFor.name, course_name: cpcForm.course_name,
        category: "Driver CPC", hours: Number(cpcForm.hours), completed_date: cpcForm.completed_date || null,
        expiry_date: cpcFor.cpc_expiry || null, provider: cpcForm.provider,
      });
      toast.success("CPC training logged"); setCpcFor(null); load();
    } catch { toast.error("Could not log training"); }
  };

  const openLc = async (d) => {
    setLcFor(d); setLcForm(lcEmpty());
    try { const { data } = await api.get(`/licence-checks?driver_id=${d.id}`); setLcHistory(data); }
    catch { setLcHistory([]); }
  };
  const saveLc = async () => {
    if (!lcForm.check_date) { toast.error("Enter the check date"); return; }
    try {
      await api.post("/licence-checks", {
        driver_id: lcFor.id, driver_name: lcFor.name,
        check_date: lcForm.check_date || null, check_code: lcForm.check_code,
        points: Number(lcForm.points) || 0, result: lcForm.result,
        next_check_due: lcForm.next_check_due || null, notes: lcForm.notes,
      });
      toast.success("Licence check logged");
      const { data } = await api.get(`/licence-checks?driver_id=${lcFor.id}`);
      setLcHistory(data); setLcForm(lcEmpty()); load();
    } catch { toast.error("Could not log licence check"); }
  };
  const deleteLc = async (id) => {
    try { await api.delete(`/licence-checks/${id}`); setLcHistory((h) => h.filter((x) => x.id !== id)); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="drivers-page">
      <Header title="Drivers" subtitle="Licence, CPC, tachograph card & weekly hours" onAdd={openNew} addTestId="add-driver-button" addLabel="Add Driver" />
      <div className="flex justify-end gap-2 mb-4 -mt-4">
        <Button data-testid="scan-licences-button" variant="outline" onClick={() => { setScanResults(null); setScanOpen(true); }} className="rounded-md gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"><Sparkles size={16} /> Scan licences</Button>
        <Button data-testid="download-drivers-pdf" variant="outline" onClick={() => downloadPdf("/reports/drivers", "drivers-report.pdf")} className="rounded-md gap-2 border-slate-300"><FileDown size={16} /> Download PDF</Button>
      </div>

      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent data-testid="scan-licences-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles size={18} className="text-blue-600" /> Scan driver licences</DialogTitle>
            <DialogDescription>Upload a batch of driving licence photos or PDFs at once. AI reads each one and creates a driver record — every record is flagged "Needs checking" for you to confirm.</DialogDescription>
          </DialogHeader>
          <label data-testid="scan-licences-drop" className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md py-8 px-3 text-sm cursor-pointer transition-colors ${scanBusy ? "border-slate-300 text-slate-400" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}>
            {scanBusy ? <Loader2 size={22} className="animate-spin" /> : <Sparkles size={22} />}
            <span>{scanBusy ? "Reading licences… this can take a moment" : "Choose licence files (you can select several)"}</span>
            <input data-testid="scan-licences-input" type="file" multiple accept="image/*,application/pdf" className="hidden" disabled={scanBusy} onChange={(e) => bulkScan(e.target.files)} />
          </label>
          {scanResults && (
            <div data-testid="scan-licences-results" className="mt-2 max-h-64 overflow-y-auto space-y-2">
              {scanResults.limit_hit && <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">Some licences weren't imported because you've reached your driver limit.</p>}
              {scanResults.created.map((r) => (
                <div key={r.id} data-testid="scan-result-row" className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <div>
                    <p className="font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.licence_number || "No licence no."}{r.licence_expiry ? ` · expires ${r.licence_expiry}` : ""}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><AlertTriangle size={10} /> Needs checking</span>
                </div>
              ))}
              {scanResults.count === 0 && <p className="text-sm text-slate-500">No drivers were created.</p>}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="scan-licences-done" variant="outline" onClick={() => setScanOpen(false)} className="rounded-md">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-2 mb-5">
        <button
          data-testid="drivers-tab-active"
          onClick={() => setTab("active")}
          className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === "active" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
        >Active ({activeItems.length})</button>
        <button
          data-testid="drivers-tab-archived"
          onClick={() => setTab("archived")}
          className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === "archived" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
        >Left / Archived ({archivedItems.length})</button>
      </div>

      {shown.length === 0 ? (
        <Empty icon={Users} text={tab === "archived" ? "No archived drivers. Drivers move here automatically once their leaving date passes." : (items.length === 0 ? "No drivers yet. Add drivers to track licences and hours." : "No active drivers.")} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((d) => {
            const over = d.weekly_hours > d.max_weekly_hours;
            const left = isLeftDriver(d);
            return (
              <div key={d.id} data-testid="driver-card" className={`bg-white border rounded-md p-5 hover:-translate-y-1 hover:shadow-sm transition-all duration-200 animate-in-up ${left ? "border-slate-200 opacity-80" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-lg text-slate-900">{d.name}</h3>
                      {d.needs_review && <span data-testid="driver-review-badge" title={d.review_fields?.length ? `AI unsure about: ${d.review_fields.join(", ")}` : "Created by AI — please confirm the details"} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><AlertTriangle size={10} /> Needs checking</span>}
                      {left && <span data-testid="driver-left-badge" className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Left {String(d.leave_date).slice(0, 10)}</span>}
                      {d.wtd_breach && <span data-testid="driver-wtd-badge" title="Averaging over 48h/week with no active working-time opt-out on record" className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700"><AlertTriangle size={10} /> 48h no opt-out</span>}
                    </div>
                    <p className="text-xs text-slate-500">{d.licence_number || "No licence no."}</p>
                  </div>
                  <div className="flex gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="export-driver-button" className="text-slate-400 hover:text-slate-900 p-1" title="Export PDF"><FileDown size={15} /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid="export-driver-summary" onClick={() => downloadPdf(`/export/driver/${d.id}`, `driver-${(d.name || "file").replace(/ /g, "_")}.pdf`)}>
                          Driver file (summary)
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="export-driver-files" onClick={() => downloadPdf(`/export/driver/${d.id}?include_files=true`, `driver-${(d.name || "file").replace(/ /g, "_")}-pack.pdf`)}>
                          Driver file + certificates
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="export-starter-pack" onClick={() => downloadPdf(`/export/driver/${d.id}/starter-pack`, `starter-pack-${(d.name || "file").replace(/ /g, "_")}.pdf`)}>
                          New starter pack (for payroll / accountant)
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="gen-self-billing" onClick={() => openSbi(d)}>
                          Self-billing invoice…
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="gen-sb-agreement" onClick={() => openAgr(d)}>
                          Self-billing agreement…
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="gen-weekly-timesheet" onClick={() => openTs(d)}>
                          Weekly time sheet…
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="open-signed-docs" onClick={() => openDocs(d)}>
                          Signed documents…
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="gen-haulage-invoice" onClick={() => openSbi(d, true)}>
                          Cross-border haulage invoice…
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="invoice-history" onClick={() => openHist(d)}>
                          Invoice history…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <button data-testid="edit-driver-button" onClick={() => openEdit(d)} className="text-slate-400 hover:text-slate-900 p-1"><Pencil size={15} /></button>
                    <button data-testid="delete-driver-button" onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Row label="Licence" status={d.licence_status} date={d.licence_expiry} />
                  <Row label="CPC" status={d.cpc_status} date={d.cpc_expiry} />
                  <Row label="Tacho Card" status={d.tacho_status} date={d.tacho_card_expiry} />
                  <Row label="Licence Check" status={d.licence_check_status} date={d.licence_check_due}
                    action={<button data-testid="log-licence-check-button" onClick={() => openLc(d)} className="text-[11px] font-semibold text-slate-400 hover:text-slate-900">+ Log check</button>} />
                  {(d.rtw_status || d.rtw_expiry) && (
                    <Row label="Right to Work" status={d.rtw_expiry ? d.rtw_status_calc : "valid"} date={d.rtw_expiry || d.rtw_status || "Verified"} />
                  )}
                </div>
                <div className="mt-3 rounded-md bg-slate-50 px-3 py-2.5" data-testid="driver-cpc-hours">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-slate-500">Driver CPC training</span>
                    <div className="flex items-center gap-2">
                      <button data-testid="log-cpc-button" onClick={() => openCpc(d)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">+ Log</button>
                      <span className={`font-bold ${(d.cpc_hours || 0) >= 35 ? "text-green-700" : "text-amber-600"}`}>{(d.cpc_hours || 0).toFixed(0)} / 35h</span>
                    </div>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                    <div data-testid="cpc-progress-bar" className={`h-full rounded-full transition-all duration-500 ${(d.cpc_hours || 0) >= 35 ? "bg-green-600" : "bg-amber-500"}`} style={{ width: `${Math.min(100, ((d.cpc_hours || 0) / 35) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {(d.cpc_hours || 0) >= 35 ? "Periodic training complete for this cycle" : `${Math.max(0, 35 - (d.cpc_hours || 0)).toFixed(0)}h remaining`}
                    {d.cpc_expiry ? ` · renews ${d.cpc_expiry}` : ""}
                  </p>
                </div>
                {d.penalty_points > 0 && (
                  <div className="mt-2 text-xs text-slate-500">Licence points: <span className="font-semibold text-slate-700">{d.penalty_points}</span>{d.licence_check_code ? ` · Check code ${d.licence_check_code}` : ""}</div>
                )}
                <div className={`mt-4 flex items-center justify-between rounded-md px-3 py-2 text-sm ${over ? "bg-red-50 text-red-700" : "bg-slate-50 text-slate-600"}`}>
                  <span className="flex items-center gap-1.5"><Clock size={15} /> Weekly hours</span>
                  <span className="font-bold">{d.weekly_hours} / {d.max_weekly_hours}h</span>
                </div>
                {d.weekly_hours > 48 && (
                  <div data-testid="driver-optout-row" className={`mt-2 flex items-center justify-between rounded-md px-3 py-2 text-xs ${d.opt_out_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                    <span className="flex items-center gap-1.5"><FileText size={13} /> 48h opt-out</span>
                    <span className="font-semibold">{d.opt_out_active ? `Signed${d.opt_out_date ? " " + String(d.opt_out_date).slice(0, 10) : ""}` : "Not signed — non-compliant"}</span>
                  </div>
                )}
                {d.opt_out_review_soon && (
                  <div data-testid="driver-optout-review" className="mt-2 flex items-center gap-1.5 rounded-md bg-amber-50 text-amber-700 px-3 py-2 text-xs">
                    <AlertTriangle size={13} /> Opt-out due for annual re-confirm{d.opt_out_review_due ? ` (${String(d.opt_out_review_due).slice(0, 10)})` : ""}
                  </div>
                )}
                <div className="mt-3 rounded-md border border-slate-200 px-3 py-2.5" data-testid="driver-access-code">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-semibold"><Smartphone size={12} /> Driver App Access</div>
                    <button data-testid="issue-code-button" onClick={() => issueCode(d)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">{d.access_code ? "Regenerate" : "Generate code"}</button>
                  </div>
                  {d.access_code ? (
                    <div className="mt-1.5 flex items-center justify-between">
                      <span data-testid="driver-code-value" className="font-mono font-black text-lg tracking-[0.25em] text-slate-900">{d.access_code}</span>
                      <div className="flex items-center gap-2">
                        {d.assigned_vehicle_reg && <span className="text-xs text-slate-400">→ {d.assigned_vehicle_reg}</span>}
                        <button data-testid="show-qr-button" onClick={() => setQrFor(d)} title="Show QR to install & auto-login" className="text-slate-400 hover:text-slate-900"><QrCode size={17} /></button>
                      </div>
                    </div>
                  ) : <p className="text-[11px] text-slate-400 mt-1">Generate a code so this driver can log in at <span className="font-semibold">/driver</span></p>}
                </div>
                {driverTraining(d).length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-3" data-testid="driver-training-list">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5 flex items-center gap-1"><GraduationCap size={12} /> Training</p>
                    <div className="space-y-1.5">
                      {driverTraining(d).map((t) => (
                        <div key={t.id} className="flex items-center justify-between text-xs gap-2">
                          <span className="text-slate-600 truncate">{t.course_name || t.category}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-slate-400">{t.expiry_date || "—"}</span>
                            <StatusBadge status={t.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid="driver-docs-list">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1"><FileText size={12} /> Documents</p>
                    <button data-testid="add-driver-doc-button" onClick={() => openDoc(d)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">+ Add</button>
                  </div>
                  {driverDocs(d).length > 0 ? (
                    <div className="space-y-1.5">
                      {driverDocs(d).map((x) => (
                        <div key={x.id} data-testid="driver-doc-item" className="flex items-center justify-between text-xs gap-2">
                          <span className="text-slate-600 truncate"><span className="text-slate-400">{x.doc_type}:</span> {x.title}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-slate-400">{x.expiry_date || ""}</span>
                            <button data-testid="delete-driver-doc-button" onClick={() => removeDoc(x.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={12} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-slate-400">No documents yet.</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Driver" : "Add Driver"}</DialogTitle><DialogDescription className="sr-only">Driver details form</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3">
              <p className="text-xs font-semibold text-blue-900 mb-2">Scan a driving licence, CPC/DQC card or tacho card — AI fills the fields below</p>
              <FileUpload testid="drv-licence-ai" label="Upload driving licence (photo or PDF)" extractType="driver_licence"
                onExtract={(f) => setForm((p) => ({ ...p, name: f.name ?? p.name, licence_number: f.licence_number ?? p.licence_number, licence_expiry: f.licence_expiry ?? p.licence_expiry, home_address: f.home_address ?? p.home_address }))}
                onCreateDraft={editId ? undefined : createDriverDraft}
                attachments={form.licence_attachments || []} onChange={(a) => setForm((p) => ({ ...p, licence_attachments: a }))} />
              {editId && (form.licence_attachments || []).length > 0 && (
                <div className="mt-2"><p className="text-[11px] font-semibold text-slate-500 mb-1">Filed licence documents</p><AttachmentThumbs attachments={form.licence_attachments} /></div>
              )}
            </div>
            <Field label="Name *"><Input data-testid="drv-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" /></Field>
            <Field label="Licence Number"><Input data-testid="drv-licence-no" value={form.licence_number} onChange={(e) => setForm({ ...form, licence_number: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Licence Expiry"><DatePicker testid="drv-licence-exp" value={form.licence_expiry} onChange={(v) => setForm({ ...form, licence_expiry: v })} /></Field>
              <Field label="CPC Expiry"><DatePicker testid="drv-cpc-exp" value={form.cpc_expiry} onChange={(v) => setForm({ ...form, cpc_expiry: v })} /></Field>
            </div>
            <FileUpload testid="drv-cpc-ai" label="Upload CPC / DQC card — AI reads the expiry" extractType="cpc"
              onExtract={(f) => setForm((p) => ({ ...p, cpc_expiry: f.cpc_expiry ?? p.cpc_expiry, name: f.name ?? p.name }))}
              attachments={[]} onChange={() => {}} />
            <Field label="Tacho Card Expiry"><DatePicker testid="drv-tacho-exp" value={form.tacho_card_expiry} onChange={(v) => setForm({ ...form, tacho_card_expiry: v })} /></Field>
            <FileUpload testid="drv-tacho-ai" label="Upload driver tacho card — AI reads the expiry" extractType="tacho_card"
              onExtract={(f) => setForm((p) => ({ ...p, tacho_card_expiry: f.tacho_card_expiry ?? p.tacho_card_expiry, name: f.name ?? p.name }))}
              attachments={[]} onChange={() => {}} />
            <Field label="Assigned Vehicle (for driver app)">
              <Select value={form.assigned_vehicle_reg || "none"} onValueChange={(v) => setForm({ ...form, assigned_vehicle_reg: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="drv-assigned-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{vehicles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer mt-2 select-none">
                <input type="checkbox" data-testid="drv-vehicle-lock" checked={!!form.vehicle_locked}
                  onChange={(e) => setForm({ ...form, vehicle_locked: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 accent-slate-900" />
                Lock to this vehicle — stop the driver switching it in the app
              </label>
            </Field>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Employment</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Start date (joined)"><DatePicker testid="drv-start-date" value={form.start_date || ""} onChange={(v) => setForm({ ...form, start_date: v })} /></Field>
                <Field label="Leaving date"><DatePicker testid="drv-leave-date" value={form.leave_date || ""} onChange={(v) => setForm({ ...form, leave_date: v })} /></Field>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Once the leaving date passes, the driver moves to <span className="font-semibold">Left / Archived</span> and drops off your active roster & compliance score.</p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Licence Checking (DVLA / NDLS)</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Last Check Date"><DatePicker testid="drv-check-date" value={form.licence_check_date} onChange={(v) => setForm({ ...form, licence_check_date: v })} /></Field>
                <Field label="Next Check Due"><DatePicker testid="drv-check-due" value={form.licence_check_due} onChange={(v) => setForm({ ...form, licence_check_due: v })} /></Field>
                <Field label="Check Code"><Input data-testid="drv-check-code" value={form.licence_check_code} onChange={(e) => setForm({ ...form, licence_check_code: e.target.value })} placeholder="DVLA share code" /></Field>
                <Field label="Penalty Points"><Input data-testid="drv-points" type="number" value={form.penalty_points} onChange={(e) => setForm({ ...form, penalty_points: e.target.value })} /></Field>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Licence detail &amp; self-billing</p>
              <p className="text-[11px] text-slate-400 mb-3">Used to auto-fill the Self-Billing Services Agreement (export dropdown → "Self-billing agreement…").</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Licence issue number"><Input data-testid="drv-licence-issue" value={form.licence_issue_number} onChange={(e) => setForm({ ...form, licence_issue_number: e.target.value })} placeholder="e.g. 74" /></Field>
                <Field label="Tacho card number"><Input data-testid="drv-tacho-no" value={form.tacho_card_number} onChange={(e) => setForm({ ...form, tacho_card_number: e.target.value })} /></Field>
                <Field label="Full licence categories"><Input data-testid="drv-lic-cats" value={form.licence_categories} onChange={(e) => setForm({ ...form, licence_categories: e.target.value })} placeholder="e.g. B, BE, C, CE, D1..." /></Field>
                <Field label="Provisional categories"><Input data-testid="drv-lic-cats-prov" value={form.licence_categories_provisional} onChange={(e) => setForm({ ...form, licence_categories_provisional: e.target.value })} placeholder="e.g. D, DE" /></Field>
              </div>
              <div className="mt-4">
                <Field label="Endorsement history"><Input data-testid="drv-endorsements" value={form.endorsement_history} onChange={(e) => setForm({ ...form, endorsement_history: e.target.value })} placeholder="e.g. SP50 — 3 pts, expired 10/10/2025 (or leave blank if none)" /></Field>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 items-end">
                <label data-testid="drv-vat-registered" className="flex items-center gap-2.5 cursor-pointer select-none pb-2">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={form.vat_registered} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} />
                  <span className="text-sm font-medium text-slate-700">Driver is VAT registered</span>
                </label>
                {form.vat_registered && (
                  <Field label="VAT number"><Input data-testid="drv-vat-number" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></Field>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Weekly Hours"><Input data-testid="drv-hours" type="number" step="0.5" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })} /></Field>
              <Field label="Max Weekly Hours"><Input data-testid="drv-max-hours" type="number" step="0.5" value={form.max_weekly_hours} onChange={(e) => setForm({ ...form, max_weekly_hours: e.target.value })} /></Field>
            </div>
            <div className="border-t border-slate-100 pt-4" data-testid="drv-optout-section">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">48-Hour Working Week Opt-Out (Working Time Directive)</p>
              <label data-testid="drv-optout-signed" className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={form.opt_out_signed} onChange={(e) => setForm({ ...form, opt_out_signed: e.target.checked })} />
                <span className="text-sm font-medium text-slate-700">Driver has signed a 48-hour opt-out agreement</span>
              </label>
              {form.opt_out_signed && (
                <>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <Field label="Date signed"><DatePicker testid="drv-optout-date" value={form.opt_out_date} onChange={(v) => setForm({ ...form, opt_out_date: v })} /></Field>
                    <Field label="Withdrawn date (if any)"><DatePicker testid="drv-optout-withdrawn" value={form.opt_out_withdrawn_date} onChange={(v) => setForm({ ...form, opt_out_withdrawn_date: v })} /></Field>
                  </div>
                  <div className="mt-3">
                    <Field label="Signed opt-out document"><FileUpload testid="drv-optout-upload" attachments={form.opt_out_attachments} onChange={(a) => setForm({ ...form, opt_out_attachments: a })} /></Field>
                  </div>
                </>
              )}
              <p className="text-[11px] text-slate-400 mt-2">Drivers averaging over 48 hours a week must have a signed opt-out on record. Without an active opt-out, they're flagged non-compliant. Setting a withdrawn date (today or earlier) ends the opt-out.</p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Right to Work (UK / Ireland / EU)</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Nationality"><Input data-testid="drv-nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} placeholder="e.g. British, Polish" /></Field>
                <Field label="Right to work status">
                  <Select value={form.rtw_status || "none"} onValueChange={(v) => setForm({ ...form, rtw_status: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="drv-rtw-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      {RTW_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Document type">
                  <Select value={form.rtw_document_type || "none"} onValueChange={(v) => setForm({ ...form, rtw_document_type: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="drv-rtw-doctype"><SelectValue placeholder="Select document" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      {RTW_DOC_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Document expiry">
                  <DatePicker testid="drv-rtw-expiry" value={form.rtw_expiry} onChange={(v) => setForm({ ...form, rtw_expiry: v })} disabled={RTW_PERMANENT.includes(form.rtw_status)} />
                </Field>
                <Field label="Date verified"><DatePicker testid="drv-rtw-verified-date" value={form.rtw_verified_date} onChange={(v) => setForm({ ...form, rtw_verified_date: v })} /></Field>
                <Field label="Verified by"><Input data-testid="drv-rtw-verified-by" value={form.rtw_verified_by} onChange={(e) => setForm({ ...form, rtw_verified_by: e.target.value })} placeholder="Who checked it" /></Field>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">{RTW_PERMANENT.includes(form.rtw_status) ? "Citizens have a permanent right to work — leave expiry blank." : "Leave expiry blank if the right to work has no end date. Expired or expiring within 30 days is flagged High Risk."}</p>
              <div className="mt-3">
                <Field label="Copy of document (passport / share code / visa)"><FileUpload testid="drv-rtw-upload" attachments={form.rtw_attachments} onChange={(a) => setForm({ ...form, rtw_attachments: a })} /></Field>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4" data-testid="drv-personal-section">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Personal details</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Date of birth"><DatePicker testid="drv-dob" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} toYear={new Date().getFullYear()} /></Field>
                <Field label="NI / PPSN number"><Input data-testid="drv-ni-ppsn" value={form.ni_ppsn} onChange={(e) => setForm({ ...form, ni_ppsn: e.target.value })} placeholder="National Insurance / PPS number" /></Field>
                <Field label="Personal phone"><Input data-testid="drv-personal-phone" value={form.personal_phone} onChange={(e) => setForm({ ...form, personal_phone: e.target.value })} /></Field>
                <Field label="Personal email"><Input data-testid="drv-personal-email" type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} /></Field>
              </div>
              <div className="mt-3">
                <Field label="Home address"><Input data-testid="drv-home-address" value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} placeholder="House, street, town, postcode" /></Field>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4" data-testid="drv-emergency-section">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Emergency contact / next of kin</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Contact name"><Input data-testid="drv-ec-name" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></Field>
                <Field label="Relationship"><Input data-testid="drv-ec-relationship" value={form.emergency_contact_relationship} onChange={(e) => setForm({ ...form, emergency_contact_relationship: e.target.value })} placeholder="e.g. Spouse, Parent" /></Field>
              </div>
              <div className="mt-3">
                <Field label="Contact phone"><Input data-testid="drv-ec-phone" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></Field>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4" data-testid="drv-engagement-section">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Engagement type</p>
              <Field label="How is this driver engaged?">
                <Select value={form.engagement_type || "Employee"} onValueChange={(v) => setForm({ ...form, engagement_type: v })}>
                  <SelectTrigger data-testid="drv-engagement-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENGAGEMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {String(form.engagement_type).startsWith("Subcontractor") && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-3" data-testid="drv-subcontractor-fields">
                  <p className="text-[11px] text-amber-800">Limited-company subcontractor. Use the printable "Subcontractor / Limited Company Engagement Form" and "Contract for Services" in Office → Compliance Docs.</p>
                  <Field label="Registered company name"><Input data-testid="drv-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={isIE ? "CRO number" : "Companies House no."}><Input data-testid="drv-company-crn" value={form.company_reg_number} onChange={(e) => setForm({ ...form, company_reg_number: e.target.value })} /></Field>
                    <Field label={isIE ? "Company tax reference" : "Corporation Tax UTR"}><Input data-testid="drv-company-utr" value={form.company_utr} onChange={(e) => setForm({ ...form, company_utr: e.target.value })} /></Field>
                    <Field label="VAT number (if any)"><Input data-testid="drv-company-vat" value={form.company_vat_number} onChange={(e) => setForm({ ...form, company_vat_number: e.target.value })} /></Field>
                    <Field label={isIE ? "Employment status" : "IR35 status"}>
                      <Select value={form.ir35_status || "none"} onValueChange={(v) => setForm({ ...form, ir35_status: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="drv-ir35-status"><SelectValue placeholder="Not assessed" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not assessed</SelectItem>
                          {isIE
                            ? [<SelectItem key="se" value="Self-employed">Self-employed</SelectItem>, <SelectItem key="emp" value="Employee (reclassify)">Employee (reclassify)</SelectItem>]
                            : [<SelectItem key="out" value="Outside IR35">Outside IR35</SelectItem>, <SelectItem key="in" value="Inside IR35">Inside IR35</SelectItem>]}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Registered company address"><Input data-testid="drv-company-address" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} /></Field>
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 pt-4" data-testid="drv-starter-section">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">{isIE ? "New starter — Revenue & tax" : "New starter — Tax & payroll (HMRC)"}</p>
              <p className="text-[11px] text-slate-400 mb-3">{isIE ? "For setting up payroll. The employment is registered via ROS to receive the Revenue Payroll Notification (RPN). See the printable \"New Employee / Starter Form (Ireland)\" in Office → Compliance Docs." : "For setting up payroll and the correct tax code. See the printable \"New Employee / Starter Form\" in Office → Compliance Docs."}</p>
              {isIE ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Tax credit / cut-off (if known)"><Input data-testid="drv-tax-code" value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} /></Field>
                    <Field label="Notes"><Input data-testid="drv-taxable-benefits" value={form.taxable_benefits} onChange={(e) => setForm({ ...form, taxable_benefits: e.target.value })} placeholder="e.g. other employment this year" /></Field>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">PPS number is captured under Personal details. Make sure the driver is registered with Revenue (myAccount) so the RPN can be issued.</p>
                </>
              ) : (
                <>
                  <label data-testid="drv-p45" className="flex items-center gap-2.5 cursor-pointer select-none mb-3">
                    <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={!!form.p45_provided} onChange={(e) => setForm({ ...form, p45_provided: e.target.checked })} />
                    <span className="text-sm font-medium text-slate-700">P45 from previous job provided</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Starter checklist statement">
                      <Select value={form.starter_checklist_statement || "none"} onValueChange={(v) => setForm({ ...form, starter_checklist_statement: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="drv-starter-statement"><SelectValue placeholder="Select statement" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not set</SelectItem>
                          {STARTER_STATEMENTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Tax code"><Input data-testid="drv-tax-code" value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} placeholder="e.g. 1257L" /></Field>
                  </div>
                  <div className="mt-3">
                    <Field label="Taxable benefits (if any)"><Input data-testid="drv-taxable-benefits" value={form.taxable_benefits} onChange={(e) => setForm({ ...form, taxable_benefits: e.target.value })} placeholder="e.g. Jobseeker's Allowance, Incapacity Benefit" /></Field>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <Field label="Student loan plan">
                      <Select value={form.student_loan_plan || "none"} onValueChange={(v) => setForm({ ...form, student_loan_plan: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="drv-student-loan"><SelectValue placeholder="No student loan" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No student loan</SelectItem>
                          {STUDENT_LOAN_PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <div className="flex items-end pb-2">
                      <label data-testid="drv-pg-loan" className="flex items-center gap-2.5 cursor-pointer select-none">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={!!form.postgraduate_loan} onChange={(e) => setForm({ ...form, postgraduate_loan: e.target.checked })} />
                        <span className="text-sm font-medium text-slate-700">Postgraduate loan</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 pt-4" data-testid="drv-bank-section">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Bank details (salary payments)</p>
              <Field label="Account holder name"><Input data-testid="drv-bank-holder" value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <Field label="Bank / building society"><Input data-testid="drv-bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></Field>
                <Field label="Account number">
                  <div className="relative">
                    <Input data-testid="drv-bank-account" type={showAcct ? "text" : "password"} value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} placeholder="8 digits" className="pr-9" />
                    <button type="button" data-testid="drv-bank-account-toggle" onClick={() => setShowAcct((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title={showAcct ? "Hide" : "Show"}>
                      {showAcct ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </Field>
                <Field label="Sort code"><Input data-testid="drv-bank-sort" value={form.bank_sort_code} onChange={(e) => setForm({ ...form, bank_sort_code: e.target.value })} placeholder="00-00-00" /></Field>
              </div>
              <div className="mt-3">
                <Field label="Supporting documents (P45, bank proof, share code, etc.)"><FileUpload testid="drv-starter-upload" attachments={form.starter_attachments} onChange={(a) => setForm({ ...form, starter_attachments: a })} /></Field>
              </div>
            </div>

            <DialogFooter><Button data-testid="save-driver-button" type="submit" className="bg-black hover:bg-slate-800">{editId ? "Save Changes" : "Add Driver"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!docFor} onOpenChange={(o) => !o && setDocFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Add document — {docFor?.name}</DialogTitle><DialogDescription className="sr-only">Driver document form</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <Field label="Type">
              <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                <SelectTrigger data-testid="ddoc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DRIVER_DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Title *"><Input data-testid="ddoc-title" value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} placeholder="e.g. Speeding infringement — M6" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Reference"><Input data-testid="ddoc-ref" value={docForm.reference} onChange={(e) => setDocForm({ ...docForm, reference: e.target.value })} /></Field>
              <Field label="Date / Expiry"><DatePicker testid="ddoc-date" value={docForm.expiry_date} onChange={(v) => setDocForm({ ...docForm, expiry_date: v })} /></Field>
            </div>
            <Field label="Notes"><Input data-testid="ddoc-notes" value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} /></Field>
            <Field label="Scan / File"><FileUpload testid="ddoc-upload" smartDetect genericMap attachments={docForm.attachments} onChange={(a) => setDocForm({ ...docForm, attachments: a })} onExtract={(f) => setDocForm((prev) => ({ ...prev, ...(f.title ? { title: f.title } : {}), ...(f.reference ? { reference: f.reference } : {}), ...(f.expiry_date ? { expiry_date: f.expiry_date } : {}), ...(f.doc_type && DRIVER_DOC_TYPES.includes(f.doc_type) ? { doc_type: f.doc_type } : {}) }))} /></Field>
            <DialogFooter><Button data-testid="save-driver-doc-button" onClick={saveDoc} disabled={!docForm.title} className="bg-black hover:bg-slate-800">Add Document</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cpcFor} onOpenChange={(o) => !o && setCpcFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Log CPC training — {cpcFor?.name}</DialogTitle><DialogDescription>Adds a Driver CPC training record; the progress bar updates automatically.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <Field label="Course / module *"><Input data-testid="cpc-course" value={cpcForm.course_name} onChange={(e) => setCpcForm({ ...cpcForm, course_name: e.target.value })} placeholder="e.g. Drivers' Hours & WTD" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Hours *"><Input data-testid="cpc-hours" type="number" step="0.5" value={cpcForm.hours} onChange={(e) => setCpcForm({ ...cpcForm, hours: e.target.value })} placeholder="7" /></Field>
              <Field label="Completed *"><DatePicker testid="cpc-date" value={cpcForm.completed_date} onChange={(v) => setCpcForm({ ...cpcForm, completed_date: v })} /></Field>
            </div>
            <Field label="Provider"><Input data-testid="cpc-provider" value={cpcForm.provider} onChange={(e) => setCpcForm({ ...cpcForm, provider: e.target.value })} placeholder="Training centre" /></Field>
            <DialogFooter><Button data-testid="save-cpc-button" onClick={saveCpc} disabled={!cpcForm.course_name || !cpcForm.hours} className="bg-black hover:bg-slate-800">Log Training</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!qrFor} onOpenChange={(o) => !o && setQrFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Driver app QR — {qrFor?.name}</DialogTitle>
            <DialogDescription>Ask the driver to scan this with their phone camera. It opens the app and fills in their code automatically.</DialogDescription>
          </DialogHeader>
          {qrFor && (
            <div className="flex flex-col items-center gap-4 py-2" data-testid="driver-qr">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <QRCodeSVG value={`${window.location.origin}/driver?code=${qrFor.access_code}`} size={200} level="M" />
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400 uppercase tracking-widest">Access code</p>
                <p className="font-mono font-black text-xl tracking-[0.25em] text-slate-900">{qrFor.access_code}</p>
              </div>
              <p className="text-[11px] text-slate-400 text-center">On the phone, after it opens they can tap <span className="font-semibold">Add to Home Screen</span> to install HaulCheck.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!lcFor} onOpenChange={(o) => !o && setLcFor(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Licence check log — {lcFor?.name}</DialogTitle>
            <DialogDescription>Record each DVLA / NDLS licence check. The latest check updates the driver's headline status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Check date *"><DatePicker testid="lc-date" value={lcForm.check_date} onChange={(v) => setLcForm({ ...lcForm, check_date: v })} /></Field>
              <Field label="Next check due"><DatePicker testid="lc-due" value={lcForm.next_check_due} onChange={(v) => setLcForm({ ...lcForm, next_check_due: v })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Check / share code"><Input data-testid="lc-code" value={lcForm.check_code} onChange={(e) => setLcForm({ ...lcForm, check_code: e.target.value })} placeholder="DVLA share code" /></Field>
              <Field label="Penalty points"><Input data-testid="lc-points" type="number" value={lcForm.points} onChange={(e) => setLcForm({ ...lcForm, points: e.target.value })} /></Field>
            </div>
            <Field label="Result">
              <Select value={lcForm.result} onValueChange={(v) => setLcForm({ ...lcForm, result: v })}>
                <SelectTrigger data-testid="lc-result"><SelectValue /></SelectTrigger>
                <SelectContent>{LC_RESULTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Notes"><Input data-testid="lc-notes" value={lcForm.notes} onChange={(e) => setLcForm({ ...lcForm, notes: e.target.value })} placeholder="e.g. SP30 x2, expires 2027" /></Field>
            <DialogFooter><Button data-testid="save-licence-check-button" onClick={saveLc} className="bg-black hover:bg-slate-800">Log Check</Button></DialogFooter>
            <div className="border-t border-slate-100 pt-3" data-testid="lc-history">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Check history ({lcHistory.length})</p>
              {lcHistory.length === 0 ? <p className="text-xs text-slate-400">No checks logged yet.</p> : (
                <div className="space-y-1.5">
                  {lcHistory.map((c) => (
                    <div key={c.id} data-testid="lc-history-item" className="flex items-center gap-2 text-xs border border-slate-100 rounded-md px-3 py-2">
                      <span className="font-semibold text-slate-700 shrink-0">{c.check_date || "—"}</span>
                      <span className="text-slate-500 min-w-0 flex-1 truncate">
                        {(LC_RESULTS.find(([v]) => v === c.result) || [null, c.result])[1]}
                        {c.points ? ` · ${c.points} pts` : ""}{c.check_code ? ` · ${c.check_code}` : ""}
                      </span>
                      <button data-testid="lc-delete" onClick={() => deleteLc(c.id)} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sbi.open} onOpenChange={(o) => setSbi((s) => ({ ...s, open: o }))}>
        <DialogContent className="sm:max-w-md" data-testid="sbi-dialog">
          <DialogHeader>
            <DialogTitle>Self-billing invoice</DialogTitle>
            <DialogDescription>Generates a numbered invoice (next SB-#### &amp; today's date) pre-filled with your company details and {sbi.driver?.name || "the sub-contractor"}'s details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label data-testid="sbi-haulage" className="flex items-center gap-2.5 cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={sbi.haulage} onChange={(e) => setSbi((s) => ({ ...s, haulage: e.target.checked }))} />
              <span className="text-sm font-medium text-slate-700">Cross-border haulage (route &amp; load-ref layout)</span>
            </label>
            <label data-testid="sbi-vat" className="flex items-center gap-2.5 cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={sbi.vat} onChange={(e) => setSbi((s) => ({ ...s, vat: e.target.checked }))} />
              <span className="text-sm font-medium text-slate-700">Sub-contractor is VAT registered</span>
            </label>
            <label data-testid="sbi-cis" className="flex items-center gap-2.5 cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={sbi.cis} onChange={(e) => setSbi((s) => ({ ...s, cis: e.target.checked }))} />
              <span className="text-sm font-medium text-slate-700">Apply CIS deduction</span>
            </label>
            {sbi.cis && (
              <Field label="CIS deduction rate">
                <Select value={String(sbi.cis_rate)} onValueChange={(v) => setSbi((s) => ({ ...s, cis_rate: Number(v) }))}>
                  <SelectTrigger data-testid="sbi-cis-rate"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20% (registered)</SelectItem>
                    <SelectItem value="30">30% (unregistered)</SelectItem>
                    <SelectItem value="0">0% (gross payment status)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSbi((s) => ({ ...s, open: false }))}>Cancel</Button>
            <Button data-testid="sbi-generate" onClick={genSbi} className="bg-black hover:bg-slate-800">Generate invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={agr.open} onOpenChange={(o) => setAgr((s) => ({ ...s, open: o }))}>
        <DialogContent className="sm:max-w-md" data-testid="agr-dialog">
          <DialogHeader>
            <DialogTitle>Self-billing agreement</DialogTitle>
            <DialogDescription>The HMRC/Revenue self-billing authorisation, pre-filled with your company details and {agr.driver?.name || "the driver"}'s licence, VAT status and bank details. Download it or send it to the driver to e-sign in their phone app.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Currency">
              <Select value={agr.currency} onValueChange={(v) => setAgr((s) => ({ ...s, currency: v }))}>
                <SelectTrigger data-testid="agr-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GBP">£ Pounds (GBP)</SelectItem>
                  <SelectItem value="EUR">€ Euro (EUR)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <p className="text-xs text-slate-500">Governing law &amp; tax authority follow your account region ({isIE ? "Ireland / Revenue" : "UK / HMRC"}). VAT wording follows the driver's VAT-registered flag.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAgr((s) => ({ ...s, open: false }))}>Cancel</Button>
            <Button data-testid="agr-download" variant="outline" onClick={genAgr} className="border-slate-300">Download PDF</Button>
            <Button data-testid="agr-send-sign" onClick={sendAgrToSign} disabled={agr.sending} className="bg-black hover:bg-slate-800">
              {agr.sending ? "Sending…" : "Send to driver to sign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ts.open} onOpenChange={(o) => setTs((s) => ({ ...s, open: o }))}>
        <DialogContent className="sm:max-w-md" data-testid="ts-dialog">
          <DialogHeader>
            <DialogTitle>Weekly time sheet</DialogTitle>
            <DialogDescription>A blank Mon–Sun grid for {ts.driver?.name || "the driver"} to fill in, pre-filled with your company name. Set the agreed rate and it calculates the total claim. Download it or send it for the driver to e-sign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Week ending">
              <DatePicker testid="ts-week-ending" value={ts.week_ending} onChange={(v) => setTs((s) => ({ ...s, week_ending: v }))} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Currency">
                <Select value={ts.currency} onValueChange={(v) => setTs((s) => ({ ...s, currency: v }))}>
                  <SelectTrigger data-testid="ts-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GBP">£ Pounds (GBP)</SelectItem>
                    <SelectItem value="EUR">€ Euro (EUR)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Rate basis">
                <Select value={ts.rate_basis} onValueChange={(v) => setTs((s) => ({ ...s, rate_basis: v }))}>
                  <SelectTrigger data-testid="ts-rate-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hour">Per hour</SelectItem>
                    <SelectItem value="day">Per day</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Agreed rate (${ts.currency === "EUR" ? "€" : "£"})`}>
                <Input data-testid="ts-rate" type="number" step="0.01" value={ts.rate} onChange={(e) => setTs((s) => ({ ...s, rate: e.target.value }))} placeholder="e.g. 18.50" />
              </Field>
              <Field label={ts.rate_basis === "day" ? "Days worked (optional)" : "Total hours (optional)"}>
                <Input data-testid="ts-qty" type="number" step="0.25" value={ts.qty} onChange={(e) => setTs((s) => ({ ...s, qty: e.target.value }))} placeholder={ts.rate_basis === "day" ? "e.g. 5" : "e.g. 45"} />
              </Field>
            </div>
            <p className="text-xs text-slate-500">The daily grid is left blank for the driver to complete. If you enter the rate and {ts.rate_basis === "day" ? "days" : "hours"}, the total claim is calculated on the sheet.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setTs((s) => ({ ...s, open: false }))}>Cancel</Button>
            <Button data-testid="ts-download" variant="outline" onClick={genTs} className="border-slate-300">Download PDF</Button>
            <Button data-testid="ts-send-sign" onClick={sendTsToSign} disabled={ts.sending} className="bg-black hover:bg-slate-800">
              {ts.sending ? "Sending…" : "Send to driver to sign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={docs.open} onOpenChange={(o) => setDocs((s) => ({ ...s, open: o }))}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="signed-docs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSignature size={18} /> Signed documents</DialogTitle>
            <DialogDescription>Every agreement, time sheet and contract sent to {docs.driver?.name || "this driver"} to sign — view or reprint the signed copy any time.</DialogDescription>
          </DialogHeader>
          {docs.list === null ? (
            <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>
          ) : docs.list.length === 0 ? (
            <div className="py-10 text-center" data-testid="signed-docs-empty">
              <FileSignature size={28} className="mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500 text-sm">Nothing sent for signing yet. Use the export menu to send an agreement or time sheet.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 text-xs text-slate-500 pb-2">
                <span data-testid="docs-signed-count"><b className="text-emerald-600">{docs.list.filter((a) => a.status === "signed").length}</b> signed</span>
                <span data-testid="docs-pending-count"><b className="text-amber-600">{docs.list.filter((a) => a.status !== "signed").length}</b> pending</span>
              </div>
              <ul className="space-y-2">
                {docs.list.map((a) => (
                  <li key={a.id} data-testid={`signed-doc-${a.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">{a.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {a.status === "signed"
                          ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 size={12} /> Signed {a.signed_at ? new Date(a.signed_at).toLocaleDateString() : ""}{a.signer_name ? ` by ${a.signer_name}` : ""}</span>
                          : <span className="text-amber-600">Pending · sent {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : ""}</span>}
                      </p>
                    </div>
                    {a.status === "signed" ? (
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <Button data-testid={`download-signed-${a.id}`} size="sm" variant="outline" className="gap-1.5 border-slate-300"
                          onClick={() => downloadPdf(`/form-assignments/${a.id}/signed`, `${(a.title || "document").replace(/ /g, "_")}-signed.pdf`)}>
                          <Download size={14} /> Signed PDF
                        </Button>
                        {a.source_type === "weekly_timesheet" && (
                          a.invoiced_invoice_no ? (
                            <span data-testid={`invoiced-${a.id}`} className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={12} /> Invoiced {a.invoiced_invoice_no}</span>
                          ) : (
                            <Button data-testid={`invoice-from-ts-${a.id}`} size="sm" className="gap-1.5 bg-slate-900 hover:bg-slate-800"
                              onClick={() => invoiceFromTimesheet(a, docs.driver)}>
                              <ReceiptText size={14} /> Create invoice
                            </Button>
                          )
                        )}
                      </div>
                    ) : (
                      <span className="shrink-0 text-xs text-slate-400 px-2">Awaiting driver</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocs((s) => ({ ...s, open: false }))}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hist.open} onOpenChange={(o) => setHist((h) => ({ ...h, open: o }))}>
        <DialogContent className="sm:max-w-2xl" data-testid="sbi-history-dialog">
          <DialogHeader>
            <DialogTitle>Self-billing invoices — {hist.driver?.name}</DialogTitle>
            <DialogDescription>Reprint, mark as paid, or email any invoice raised for this haulier.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto -mx-1 px-1">
            {hist.loading ? <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
              : hist.items.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No invoices generated yet for this haulier.</p>
              : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase text-slate-400 border-b">
                    <th className="py-2 pr-2">Invoice</th><th className="py-2 pr-2">Date</th><th className="py-2 pr-2">Type</th><th className="py-2 pr-2">Status</th><th className="py-2 text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {hist.items.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100" data-testid={`sbi-row-${r.invoice_no}`}>
                        <td className="py-2 pr-2 font-semibold text-slate-800">{r.invoice_no}</td>
                        <td className="py-2 pr-2 text-slate-500">{r.invoice_date}</td>
                        <td className="py-2 pr-2 text-slate-500">{r.haulage ? "Haulage" : "Standard"}{r.vat ? " · VAT" : ""}{r.cis ? ` · CIS ${r.cis_rate}%` : ""}</td>
                        <td className="py-2 pr-2">
                          <button data-testid={`sbi-paid-${r.invoice_no}`} onClick={() => togglePaid(r.id)} className={`text-xs px-2 py-0.5 rounded-full border ${r.paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{r.paid ? "Paid" : "Unpaid"}</button>
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <button title="Reprint" data-testid={`sbi-reprint-${r.invoice_no}`} onClick={() => downloadPdf(`/self-billing-invoices/${r.id}/pdf`, `${r.invoice_no}.pdf`)} className="p-1.5 text-slate-500 hover:text-slate-900"><FileDown size={16} /></button>
                          <button title="Email to haulier" data-testid={`sbi-email-${r.invoice_no}`} onClick={() => emailInvoice(r)} className="p-1.5 text-slate-500 hover:text-slate-900"><Mail size={16} /></button>
                          <button title="Delete" data-testid={`sbi-del-${r.invoice_no}`} onClick={() => delInvoice(r.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, status, date, action }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500 flex items-center gap-2">{label}{action}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">{date || "—"}</span>
        <StatusBadge status={status} />
      </div>
    </div>
  );
}
